import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageSquareText, Loader2, Send, CheckCircle2, History, RotateCcw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from './adminConstants';

const TEMPLATE_AFILIADOS_LINK = `Olá, {nome}! 👋

Novidade para você vender mais: agora dá para montar sua carteira de afiliados sem trabalho nenhum.

Envie este link — a pessoa se cadastra sozinha, com aprovação na hora e kit completo no WhatsApp:
https://falocomodono.com.br/afiliado/cadastro

Cada afiliado seu = mais clientes na sua rede. 🚀

_FaloComODono - Parceiros_`;

const AUDIENCES = [
  { id: 'partners', label: 'Só Parceiros' },
  { id: 'affiliates', label: 'Só Afiliados' },
  { id: 'both', label: 'Parceiros + Afiliados' },
  { id: 'companies', label: 'Clientes (Empresas)' },
];

const AdminCustomBroadcast = () => {
  const { getToken } = useAuth();
  const [audience, setAudience] = useState('partners');
  const [message, setMessage] = useState('');
  const [counts, setCounts] = useState(null);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);

  const headers = useCallback(() => ({ headers: { Authorization: `Bearer ${getToken()}` } }), [getToken]);

  const fetchHistory = useCallback(() => {
    axios.get(`${API}/admin/broadcasts/history`, headers())
      .then(r => setHistory(r.data.broadcasts || [])).catch(() => {});
  }, [headers]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  useEffect(() => {
    axios.post(`${API}/admin/custom-broadcast`, { dry_run: true, audience }, headers())
      .then(r => setCounts(r.data)).catch(() => {});
  }, [audience, headers]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const fire = async () => {
    if (!window.confirm(`Enviar esta mensagem agora para ${counts?.total || 0} pessoas por WhatsApp?`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/custom-broadcast`, { message, audience }, headers());
      setProgress({ total: r.data.total, done: 0, sent_whatsapp: 0, status: 'processing' });
      pollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/admin/quick-link-broadcast/${r.data.broadcast_id}`, headers());
          setProgress(s.data);
          if (s.data.status === 'done') {
            clearInterval(pollRef.current);
            setSending(false);
            toast.success(`Comunicado enviado: ${s.data.sent_whatsapp} WhatsApp`);
            fetchHistory();
          }
        } catch { clearInterval(pollRef.current); setSending(false); }
      }, 3000);
      toast.success('Disparo iniciado (intervalo anti-spam entre envios)');
    } catch (err) {
      setSending(false);
      toast.error(err.response?.data?.detail || 'Erro ao disparar');
    }
  };

  return (
    <Card data-testid="custom-broadcast-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><MessageSquareText className="w-5 h-5 text-[#C5A059]" /> Comunicado em Massa</CardTitle>
        <p className="text-sm text-slate-500">
          Escreva uma vez, dispare para todos por WhatsApp. Use <code className="bg-slate-100 px-1 rounded">{'{nome}'}</code>,{' '}
          <code className="bg-slate-100 px-1 rounded">{'{link_relampago}'}</code> e <code className="bg-slate-100 px-1 rounded">{'{painel}'}</code> para personalizar cada mensagem.
          Para <strong>Clientes (Empresas)</strong>: {'{nome}'} = nome da empresa, {'{painel}'} = link mágico das Configurações (sem senha).
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 flex-wrap">
          {AUDIENCES.map(a => (
            <button key={a.id} onClick={() => setAudience(a.id)} data-testid={`cb-audience-${a.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${audience === a.id ? 'bg-[#0A2647] text-white border-[#0A2647]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}>
              {a.label}
            </button>
          ))}
          {counts && <span className="ml-auto text-xs text-slate-400 self-center" data-testid="cb-count">{counts.total} destinatários com WhatsApp</span>}
        </div>
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={7}
          placeholder="Escreva seu comunicado aqui... Ex: Olá, {nome}! Temos uma novidade..."
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
          data-testid="cb-message" />
        <button onClick={() => setMessage(TEMPLATE_AFILIADOS_LINK)} data-testid="cb-template-btn"
          className="text-xs text-[#C5A059] font-semibold hover:underline">
          📋 Usar modelo pronto: "Monte sua carteira de afiliados" (link de auto-cadastro)
        </button>
        {progress && progress.status === 'processing' && (
          <div className="space-y-1" data-testid="cb-progress">
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#C5A059] transition-all" style={{ width: `${(progress.done / Math.max(progress.total, 1)) * 100}%` }} />
            </div>
            <p className="text-[11px] text-slate-500">{progress.done}/{progress.total} · {progress.sent_whatsapp} enviados</p>
          </div>
        )}
        {progress && progress.status === 'done' && (
          <p className="flex items-center gap-1.5 text-xs text-emerald-700 font-semibold" data-testid="cb-done">
            <CheckCircle2 className="w-4 h-4" /> Concluído: {progress.sent_whatsapp} enviados · {progress.errors || 0} erros
          </p>
        )}
        <Button onClick={fire} disabled={sending || !message.trim() || !counts?.total}
          className="w-full bg-[#0A2647] hover:bg-[#0e3260] text-white gap-2" data-testid="cb-fire-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Disparar para {counts?.total || 0} pessoas
        </Button>

        {history.length > 0 && (
          <div className="pt-4 border-t border-slate-100" data-testid="cb-history">
            <p className="flex items-center gap-1.5 text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">
              <History className="w-3.5 h-3.5 text-[#C5A059]" /> Comunicados enviados
            </p>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {history.map(b => (
                <div key={b.id} className="p-3 bg-slate-50 rounded-xl border border-slate-100" data-testid={`cb-history-item-${b.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-[10px] text-slate-400">
                      {new Date(b.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      {' · '}{AUDIENCES.find(a => a.id === b.audience)?.label || b.audience}
                      {' · '}{b.sent_whatsapp || 0}/{b.total || 0} enviados{b.errors ? ` · ${b.errors} erros` : ''}
                    </span>
                    <button
                      onClick={() => { setAudience(b.audience || 'partners'); setMessage(b.message || ''); window.scrollTo({ top: 0, behavior: 'smooth' }); toast.info('Mensagem carregada na caixa de texto — edite o que quiser e clique em Disparar'); }}
                      className="flex items-center gap-1 text-[11px] font-semibold text-[#C5A059] hover:underline flex-shrink-0"
                      data-testid={`cb-resend-btn-${b.id}`}>
                      <RotateCcw className="w-3 h-3" /> Editar e Reenviar
                    </button>
                  </div>
                  <p className="text-xs text-slate-600 whitespace-pre-line line-clamp-3">{b.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminCustomBroadcast;
